import type { HermesAPI } from "./api";
import { endpointTransport } from "./auth";
import { arr, bool, int, num, obj, params, s, str, strings } from "./json";
import type { GatewaySocket } from "./transport";
import { withQuery } from "./transport";

// Bot 的电脑（Hermes Bot Desktop）：Linux 主机上给每个 Bot 开一个无头 Xfce 桌面（Xvnc），Bot 用电脑、开浏览器都在这里。
// 画面是 VNC（RFB）数据流：display.observe 换一张 30 秒一次性票据，再连 /api/display/ws?display_ticket=…（二进制帧）。
// 控制权（lease）：holder 是 agent（Bot 在用）或 human（有人接管）；接管期间 Bot 不操作屏幕。

export interface DisplayLease { holder: string; viewer_hash?: string; epoch?: number }

export interface DisplayStatus {
  /** 只有 Linux 主机支持 */
  supported: boolean;
  installed: boolean;
  missing: string[];
  running: boolean;
  geometry?: string;
  install_command?: string;
  /** 现在为什么不能启动（主机内存不够等），有值时不能启动 */
  blocker?: string;
  memory_available_mb?: number;
  memory_limit_mb?: number;
  lease?: DisplayLease;
}

function parseLease(raw: unknown): DisplayLease | undefined {
  const o = obj(raw);
  const holder = s(o?.holder);
  return holder ? { holder, viewer_hash: s(o?.viewer_hash), epoch: int(o?.epoch) } : undefined;
}

function parseStatus(raw: unknown): DisplayStatus {
  const o = obj(raw) ?? {};
  return {
    supported: bool(o.supported) ?? false, installed: bool(o.installed) ?? false, missing: strings(o.missing) ?? [],
    running: bool(o.running) ?? false, geometry: s(o.geometry), install_command: s(o.install_command), blocker: s(o.blocker),
    memory_available_mb: int(o.memory_available_mb), memory_limit_mb: int(o.memory_limit_mb), lease: parseLease(o.lease),
  };
}

export const Display = {
  async status(api: HermesAPI, profile?: string): Promise<DisplayStatus> {
    return parseStatus(await api.client.call("display.status", params({ profile })));
  },
  /** 启动桌面（会等到桌面起来，可能要十几秒） */
  async start(api: HermesAPI, profile?: string): Promise<DisplayStatus> {
    return parseStatus(await api.client.call("display.start", params({ profile }), { timeout: 90 }));
  },
  async stop(api: HermesAPI, profile?: string, force = false): Promise<void> {
    await api.client.call("display.stop", params({ profile, force }), { timeout: 30 });
  },
  /** 一张 JPEG 截图（data URL）；桌面没开、或者有人接管时为空 */
  async thumbnail(api: HermesAPI, profile?: string): Promise<string | undefined> {
    const url = str(obj(await api.client.call("display.thumbnail", params({ profile })))?.data_url);
    return url && url.startsWith("data:") ? url : undefined;
  },
  /** 在主机上安装桌面组件。输出走 display.install.log 事件，结束是 display.install.done；要 sudo 密码时发 display.install.sudo 请求 */
  async install(api: HermesAPI, profile?: string): Promise<void> {
    await api.client.call("display.install", params({ profile }));
  },
  /** 换一张看画面用的票据。viewerID 传这条连接上次拿到的，可以保持身份（接管状态） */
  async observe(api: HermesAPI, profile?: string, viewerID?: string): Promise<{ ticket: string; path: string; viewerID: string }> {
    const o = obj(await api.client.call("display.observe", params({ profile, viewer_id: viewerID })));
    return { ticket: s(o?.ticket) ?? "", path: s(o?.path) ?? "/api/display/ws", viewerID: s(o?.viewer_id) ?? "" };
  },
  /** 接管：之后键盘鼠标只认这个 viewer，Bot 暂停操作屏幕 */
  async takeOver(api: HermesAPI, profile: string | undefined, viewerID: string): Promise<DisplayLease | undefined> {
    const raw = obj(await api.client.call("display.lease.acquire", params({ profile, viewer_id: viewerID, reason: "TimiBot" })));
    return parseLease(raw?.lease ?? raw);
  },
  /** 交还给 Bot */
  async handBack(api: HermesAPI, profile: string | undefined, viewerID: string): Promise<void> {
    await api.client.call("display.lease.release", params({ profile, viewer_id: viewerID }));
  },
  /** 打开画面数据流（VNC/RFB 二进制帧）。票据 30 秒内用、只能用一次 */
  openStream(api: HermesAPI, ticket: { ticket: string; path: string }): Promise<GatewaySocket> {
    return endpointTransport(api.client.broker.endpoint).openSocket(withQuery(ticket.path, { display_ticket: ticket.ticket }), 20);
  },
};

// 主机状态（Hermes 网页后台“系统”页同一个接口 /api/system/stats，只有主人能看）

export interface HostStats {
  cpu_count?: number;
  cpu_percent?: number;
  load_avg?: number[];
  memory?: { total?: number; used?: number; percent?: number };
  disk?: { total?: number; used?: number; percent?: number };
}

export async function hostStats(api: HermesAPI): Promise<HostStats> {
  const o = obj(await api.client.broker.getJSON("/api/system/stats")) ?? {};
  const usage = (v: unknown) => { const u = obj(v); return u ? { total: num(u.total), used: num(u.used), percent: num(u.percent) } : undefined; };
  return {
    cpu_count: int(o.cpu_count), cpu_percent: num(o.cpu_percent),
    load_avg: (arr(o.load_avg) ?? []).map(num).filter((x): x is number => x !== undefined),
    memory: usage(o.memory), disk: usage(o.disk),
  };
}

export const ScreenAdvice = {
  /** 4 GB 以下的主机不建议开（Hermes 实测：桌面空闲约 220 MB，开一个浏览器页面再加 0.5～1 GB） */
  recommendedMB: 4096,
  isLowMemory(st?: DisplayStatus): boolean {
    return st?.memory_limit_mb !== undefined && st.memory_limit_mb < this.recommendedMB;
  },
  gb(mb: number): string { return mb >= 10240 ? `${Math.floor(mb / 1024)} GB` : `${(mb / 1024).toFixed(1)} GB`; },
  memoryLine(st?: DisplayStatus): string {
    const parts: string[] = [];
    if (st?.memory_limit_mb !== undefined) parts.push(`内存 ${this.gb(st.memory_limit_mb)}`);
    if (st?.memory_available_mb !== undefined) parts.push(`空闲 ${this.gb(st.memory_available_mb)}`);
    return parts.length ? "这台主机" + parts.join("，") : "读不到主机内存";
  },
  short(st: DisplayStatus): string {
    if (st.memory_limit_mb === undefined) return "";
    return `内存 ${this.gb(st.memory_limit_mb)} · ` + (this.isLowMemory(st) ? "配置偏低，不建议开启" : "可以开启");
  },
  /** Hermes 的拒绝原因是英文，常见的翻一下 */
  blocker(text: string): string {
    const lower = text.toLowerCase();
    if (lower.includes("memory")) return "空闲内存不足 1.5 GB";
    if (lower.includes("not installed") || lower.includes("missing")) return "桌面组件没装全";
    return text;
  },
};
